import React, { useState } from 'react'
import { Box, Button, Grid, Typography } from '@mui/material'
import { AxiosError, AxiosResponse } from 'axios'
import isEmpty from 'lodash/isEmpty'
import dayjs from 'dayjs'
import { UseMutationResult } from '@tanstack/react-query'

import { useDialog } from '../hooks'
import { ApiErrorResponse, ReportFormat, ReportParams, SbpReportParams } from '../types.ts'
import { downloadFile } from '../utils.ts'

import { Form } from './form'
import { DateRange } from './date-range.tsx'
import { Dialog } from './dialog.tsx'
import { RadioGroup } from './radio-group.tsx'
import { useSnackbarContext } from './snackbar'

export type ReportRequestFormState = ReportParams

const REPORT_FORM = 'reportForm'

export interface ReportDialogProps {
  mutation: UseMutationResult<AxiosResponse<Blob>, unknown, ReportParams, unknown>
}
export const ReportDialog = ({ mutation }: ReportDialogProps): React.ReactElement => {
  const [dateRange, setDateRange] = useState<[string?, string?]>([])
  const [format, setFormat] = useState<ReportFormat>('xlsx')
  const { showDialog, handleShowDialog, handleHideDialog } = useDialog()
  const { mutateAsync, reset } = mutation
  const { addSnack } = useSnackbarContext()

  const handleFormatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormat(event.target.value as SbpReportParams['formatReport'])
  }
  const handleReportSubmit = async () => {
    try {
      const [dateFrom, dateTo] = dateRange
      const response = await mutateAsync({
        dateTo: dayjs(dateTo, 'YYYY-MM-DD').format(`DD.MM.YYYY`),
        dateFrom: dayjs(dateFrom, 'YYYY-MM-DD').format(`DD.MM.YYYY`),
        formatReport: format,
      })

      if (response?.status === 200 && response.data) {
        const filename = response?.headers['content-disposition']?.match(/filename="([^"]+)"/i)

        downloadFile(URL.createObjectURL(response.data), filename?.[1] ?? `report_${dateFrom}-${dateTo}`)
        handleHideDialog()
        reset()
        setDateRange([])
      }

      if (response?.status !== 200) {
        addSnack({
          severity: 'error',
          message: 'Произошла ошибка при загрузке файла',
        })
      }
    } catch (axiosError) {
      let errorMessage = 'Произошла непредвиденная ошибка'

      const error = axiosError as AxiosError<ApiErrorResponse>
      if (error.isAxiosError && error.response?.data?.errorDetails) {
        errorMessage = error.response.data.errorDetails
      }

      addSnack({
        message: errorMessage,
        severity: 'error',
        timeout: 3000,
      })
    }
  }

  const disabledSubmit = isEmpty(dateRange)

  return (
    <Box display="flex" justifyContent="flex-end">
      <Button onClick={handleShowDialog} variant="outlined">
        Выгрузить данные за период
      </Button>
      {showDialog && (
        <Form<ReportRequestFormState>
          name={REPORT_FORM}
          initialValues={{
            formatReport: 'xlsx',
            dateFrom: '',
            dateTo: '',
          }}
          onSubmit={handleReportSubmit}
        >
          <Dialog
            title="Выгрузить данные за период"
            open={showDialog}
            onClose={handleHideDialog}
            content={
              <Grid container flexDirection="column" spacing={2}>
                <Grid item>
                  <Typography variant="body1" color="text.secondary">
                    Период отчёта
                  </Typography>
                </Grid>
                <Grid item>
                  <DateRange initialRange={dateRange} onChange={setDateRange} />
                </Grid>
                <Grid item>
                  <RadioGroup
                    row
                    value={format}
                    onChange={handleFormatChange}
                    label="Формат отчёта"
                    items={[
                      { title: '.xlsx', value: 'xlsx' },
                      { title: '.csv', value: 'csv' },
                    ]}
                  />
                </Grid>
              </Grid>
            }
            actions={
              <Button disabled={disabledSubmit} fullWidth onClick={handleReportSubmit}>
                Отправить заявку
              </Button>
            }
          />
        </Form>
      )}
    </Box>
  )
}
